import { OnInit, ViewContainerRef, OnDestroy, Injectable } from '@angular/core';
import { CUEServiceBase } from '../_services/cue-base.service';
import { Router } from '@angular/router';
import { DialogService } from '../_services/dialog.service';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, mergeMap } from 'rxjs/operators';
import { getUrl } from '../_shared/utils';
import { HttpClient, HttpRequest, HttpResponse, HttpEventType, HttpHeaders } from '@angular/common/http';
import { UserService } from '../_services/user.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { JWTTokenService } from '../_services/jwtToken.service';

export interface CUEServiceListViewBase {
  serviceId: number;
  id: number;
  name: string;
  active: boolean;
  startDate: string;
  endDate: string;
  fileDownloadUrl: string;
  dateCreated: string;
  dateModified: string;
  createdBy: string;
  modifiedBy: string;
  engineName: string;
}

@Injectable()
export abstract class ManagementComponentBase<TList extends CUEServiceListViewBase, TDetail> implements OnInit, OnDestroy {
  public items: TList[] = [];
  public displayItems: TList[] = [];

  public additionalColumns: ColumnProperty[] = [];

  public loading = false;
  public searchText: any;
  public showInactive = false;

  private onDestroy$ = new Subject();

  public serviceDisplayName: string;
  protected _httpClient: HttpClient;

  public downloadStates = {};

  private _hasSiteManagement: boolean;
  get hasSiteManagement() {
    return this._hasSiteManagement;
  }

  constructor(
    protected service: CUEServiceBase<TDetail, TList>,
    protected router: Router,
    protected dialogService: DialogService,
    protected viewRef: ViewContainerRef,
    protected httpClient: HttpClient,
    protected userService: UserService,
    protected spinner: NgxSpinnerService,
    protected jwtTokenService: JWTTokenService
  ) {
    this._httpClient = httpClient;
  }

  ngOnInit() {
    this.loadItems().pipe(takeUntil(this.onDestroy$)).subscribe();

      const siteManagement = this.userService.hasSinglePermission('SiteManagement').subscribe(results => {
        this._hasSiteManagement = results;
      });


    this.serviceDisplayName = this.service.getServiceDisplayName();
  }

  protected abstract GetRouteName(): string;

  private loadItems(): Observable<any> {
    this.loading = true;
    this.spinner.show();
    return this.service.getAll().pipe(
      tap(
        data => {
          this.items = data;
          this.displayItems = this.items.filter(i => i.active);
          this.loading = false;
          this.spinner.hide();
        },
        () => {
          this.dialogService.alert('Error', `Unable to load ${this.serviceDisplayName}s`, this.viewRef);
          this.loading = false;
          this.spinner.hide();
        }
      )
    );
  }

  public createNew() {
    this.router.navigate([`/portal/${this.GetRouteName()}/create`]);
  }

  public search() {
    if (this.showInactive) {
      this.displayItems = this.items;
    } else {
      this.displayItems = this.items.filter(i => i.active);
    }

    if (!this.searchText) {
      return;
    }

    const search = this.searchText.toLowerCase();
    this.displayItems = this.displayItems.filter(d =>
      (d.name ? d.name.toLowerCase().indexOf(search) !== -1 : false) ||
      (d.createdBy ? d.createdBy.toLowerCase().indexOf(search) !== -1 : false) ||
      (d.fileDownloadUrl ? d.fileDownloadUrl.toLowerCase().indexOf(search) !== -1 : false) ||
      (d.modifiedBy ? d.modifiedBy.toLowerCase().indexOf(search) !== -1 : false) ||
      (d.engineName ? d.engineName.toLowerCase().indexOf(search) !== -1 : false) ||
      (d.serviceId === +search) ||
      !search);
  }

  public delete(id: number) {
    this.dialogService.confirm('Confirm Delete', `Are you sure you want to delete this ${this.serviceDisplayName}?`, this.viewRef).then(result => {
      if (result) {
        this.loading = true;
        this.spinner.show();
        this.service
          .delete(id)
          .pipe(mergeMap(() => this.loadItems()))
          .subscribe(
            () => {
              this.loading = false;
              this.spinner.hide();
              this.dialogService.alert('Success', `${this.serviceDisplayName} has been deleted.`, this.viewRef);
            }, error => {
              this.loading = false;
              this.spinner.hide();
              this.dialogService.alert('Error', `Unable to delete ${this.serviceDisplayName}`, this.viewRef);
            }
        );
      }
    });
  }


  public async edit(id: number, serviceId: number) {
    await this.router.navigate([`/portal/${this.GetRouteName()}/`, serviceId, id]);
  }

  // TODO: This needs to be cleaned up, I'm sure there is a better way
  public checkDownloadLink(row: TList, rowIndex: number) {
    this.downloadStates[rowIndex + '-download'] = 1;
    this.downloadStates[rowIndex + '-downloadPercent'] = 0;
    const headers = new HttpHeaders({
      'Content-type': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Authorization': 'Bearer ' + this.jwtTokenService.getToken('portal')
    });
    this.httpClient.get(getUrl(`${this.GetRouteName()}/download/${row.id}`), {headers: headers}).subscribe(
      (signedUrls: any) => {
        const headUrl = signedUrls.headUrl;
        const downloadUrl = signedUrls.getUrl;
        const fileName = signedUrls.fileName;
        const options = { responseType: 'blob' as 'blob' };

        this.httpClient.head(headUrl, options).subscribe(
          (response: any) => {

            const req = new HttpRequest('GET', downloadUrl, {
              reportProgress: true,
              observe: 'events',
              responseType: 'arrayBuffer'
            });

            this._httpClient.request(req).subscribe(event => {
              // Access event stream to report progress
              if (event.type === HttpEventType.DownloadProgress) {
                // Calculate percentage done from the 'Content-Size'
                const percentDone = Math.round(100 * event.loaded / event.total);
                this.downloadStates[rowIndex + '-downloadPercent'] = percentDone;
              } else if (event instanceof HttpResponse) {
                const url = window.URL.createObjectURL(new Blob([event.body as ArrayBuffer], {type: 'application/octet-stream'}));
                const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                this.downloadStates[rowIndex + '-download'] = 3;
              }
            });
          },
          err => {
            this.dialogService.alert('Download Pending', `Your ${this.serviceDisplayName} will be ready momentarily, please check back in a few minutes. If this message persists, please contact support@connectwithcue.com`, this.viewRef);
            this.downloadStates[rowIndex + '-download'] = 0;
          });
      },
      () => {
        this.dialogService.alert('Download Error', 'There appears to be an error with your file, please contact support@connectwithcue.com, sorry about that!', this.viewRef);
        this.downloadStates[rowIndex + '-download'] = 4;
      }
    );
  }

  public regenerate (id: number) {

    this.dialogService.confirm('Are you sure?',
      'This will reactivate the service and will generate a new audio file that you\'ll have to download. Any old audio files for this service will not work.',
      this.viewRef)
        .then(
          res => {

            if (res) {

              this.spinner.show();

              this.service.regenerate(id)
                .subscribe(
                  _ => {

                    this.spinner.hide();
                    this.dialogService.alert('Success!', 'This service has been reactivated and a new audio file has been created.', this.viewRef)
                      .then(
                        res => {

                          this.ngOnInit();
                        }
                      );
                  },
                  error => {

                    this.spinner.hide();
                    this.dialogService.alert('Uh oh!', 'Something went wrong trying to reactivate that service. Please contact us at support@connectwithcue.com', this.viewRef);
                  }
                );
            }
          }
        )
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}

export interface ColumnProperty {
  propertyName: string;
  sortable: boolean;
  headerName: string;
  dataName: string;
}
